// store/index.ts
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { actionColumnsApi } from './features/action-columns.feature';
import { activeOperationsReducer } from './features/active-operations.feature';
import { advancedPlaybooksApi } from './features/custom-playbooks.feature';
import { gtmAgentsApi } from './features/gtm-agents.feature';
import { agentsApi } from './features/agents.feature';
import { searchProfilesApi } from '@/containers/search/profiles/search-profiles.api';
import { finderSearchesApi } from './features/finder-searches.feature';
import { appsumoApi } from './features/appsumo.feature';
import { identifyApi } from './features/identify.feature';
import { onboardingChecklistApi } from '@/store/features/onboarding-checklist.feature';
import { analyticsApi } from '@/store/features/analytics.feature';
import { apiKeysApi } from './features/api-keys.feature';
import { billingApi } from './features/billing.feature';
import { crmApi } from './features/crm.feature';
import { directoryApi } from './features/directory.feature';
import { emailTemplatesApi } from './features/email-templates.feature';
import { enrichmentStatusReducer } from './features/enrichment-status.feature';
import { enrichmentApi } from './features/enrichment.feature';
import { extensionApi } from './features/extension.feature';
import { leadListFoldersApi } from './features/lead-list-folders.feature';
import { leadListsApi } from './features/lead-lists.feature';
import { leadsApi } from './features/leads.feature';
import { notificationsApi } from './features/notifications.feature';
import { prospectsApi } from './features/prospects.feature';
import { onboardingCallCreditsApi } from './features/onboarding-call-credits.feature';
import { reviewCreditsApi } from './features/review-credits.feature';
import { productHuntCreditsApi } from './features/producthunt-credits.feature';
import { g2ReviewCreditsApi } from './features/g2-review-credits.feature';
import { adminOrganizationsApi } from './features/admin-organizations.feature';
import { adminCreditUsageApi } from './features/admin-credit-usage.feature';
import { adminCardTrialsApi } from './features/admin-card-trials.feature';
import { adminFinanceApi } from './features/admin-finance.feature';
import { adminRevenueApi } from './features/admin-revenue.feature';
import { adminCluChatsApi } from './features/admin-clu-chats.feature';
import { cluSharingApi } from './features/clu-sharing.feature';
import { slackApi } from './features/slack.feature';
import { workspaceConnectionsApi } from './features/workspace-connections.feature';
import { smartColumnsApi } from './features/smart-columns.feature';
import { themeReducer } from './features/theme.feature';
import { uiReducer } from './features/ui.feature';
import { companySearchApi } from './features/company-search.feature';
import { customAiColumnTemplatesApi } from './features/custom-ai-column-templates.feature';
import { userPreferencesApi } from './features/user-preferences.feature';
import { engageApi } from './features/engage.feature';
import { knowledgeApi } from './features/knowledge.feature';
import { cluMemoryApi } from './features/clu-memory.feature';
import { homeCardsApi } from './features/home-cards.feature';
// Extension side-panel plugin app (plugin.html entry) — registered globally
// so the plugin surface shares this store; unused (and tree-shaken cold) in
// the portal SPA itself.
import { pluginAgentsApi } from '@/plugin/agents-api';
import { pluginProspectApi } from '@/plugin/prospect-api';

export const store = configureStore({
  reducer: {
    theme: themeReducer,
    enrichmentStatus: enrichmentStatusReducer,
    activeOperations: activeOperationsReducer,
    ui: uiReducer,
    [apiKeysApi.reducerPath]: apiKeysApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [leadListFoldersApi.reducerPath]: leadListFoldersApi.reducer,
    [leadListsApi.reducerPath]: leadListsApi.reducer,
    [leadsApi.reducerPath]: leadsApi.reducer,
    [crmApi.reducerPath]: crmApi.reducer,
    [directoryApi.reducerPath]: directoryApi.reducer,
    [emailTemplatesApi.reducerPath]: emailTemplatesApi.reducer,
    [enrichmentApi.reducerPath]: enrichmentApi.reducer,
    [extensionApi.reducerPath]: extensionApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [prospectsApi.reducerPath]: prospectsApi.reducer,
    [smartColumnsApi.reducerPath]: smartColumnsApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [userPreferencesApi.reducerPath]: userPreferencesApi.reducer,
    [advancedPlaybooksApi.reducerPath]: advancedPlaybooksApi.reducer,
    [gtmAgentsApi.reducerPath]: gtmAgentsApi.reducer,
    [actionColumnsApi.reducerPath]: actionColumnsApi.reducer,
    [onboardingChecklistApi.reducerPath]: onboardingChecklistApi.reducer,
    [companySearchApi.reducerPath]: companySearchApi.reducer,
    [customAiColumnTemplatesApi.reducerPath]:
      customAiColumnTemplatesApi.reducer,
    [agentsApi.reducerPath]: agentsApi.reducer,
    [searchProfilesApi.reducerPath]: searchProfilesApi.reducer,
    [finderSearchesApi.reducerPath]: finderSearchesApi.reducer,
    [appsumoApi.reducerPath]: appsumoApi.reducer,
    [identifyApi.reducerPath]: identifyApi.reducer,
    [onboardingCallCreditsApi.reducerPath]: onboardingCallCreditsApi.reducer,
    [reviewCreditsApi.reducerPath]: reviewCreditsApi.reducer,
    [productHuntCreditsApi.reducerPath]: productHuntCreditsApi.reducer,
    [g2ReviewCreditsApi.reducerPath]: g2ReviewCreditsApi.reducer,
    [adminOrganizationsApi.reducerPath]: adminOrganizationsApi.reducer,
    [adminCreditUsageApi.reducerPath]: adminCreditUsageApi.reducer,
    [adminCardTrialsApi.reducerPath]: adminCardTrialsApi.reducer,
    [adminFinanceApi.reducerPath]: adminFinanceApi.reducer,
    [adminRevenueApi.reducerPath]: adminRevenueApi.reducer,
    [adminCluChatsApi.reducerPath]: adminCluChatsApi.reducer,
    [cluSharingApi.reducerPath]: cluSharingApi.reducer,
    [slackApi.reducerPath]: slackApi.reducer,
    [workspaceConnectionsApi.reducerPath]: workspaceConnectionsApi.reducer,
    [pluginProspectApi.reducerPath]: pluginProspectApi.reducer,
    [pluginAgentsApi.reducerPath]: pluginAgentsApi.reducer,
    [engageApi.reducerPath]: engageApi.reducer,
    [knowledgeApi.reducerPath]: knowledgeApi.reducer,
    [cluMemoryApi.reducerPath]: cluMemoryApi.reducer,
    [homeCardsApi.reducerPath]: homeCardsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      analyticsApi.middleware,
      apiKeysApi.middleware,
      leadListFoldersApi.middleware,
      leadListsApi.middleware,
      leadsApi.middleware,
      crmApi.middleware,
      directoryApi.middleware,
      emailTemplatesApi.middleware,
      enrichmentApi.middleware,
      extensionApi.middleware,
      billingApi.middleware,
      prospectsApi.middleware,
      smartColumnsApi.middleware,
      notificationsApi.middleware,
      userPreferencesApi.middleware,
      advancedPlaybooksApi.middleware,
      gtmAgentsApi.middleware,
      actionColumnsApi.middleware,
      onboardingChecklistApi.middleware,
      companySearchApi.middleware,
      customAiColumnTemplatesApi.middleware,
      agentsApi.middleware,
      searchProfilesApi.middleware,
      finderSearchesApi.middleware,
      appsumoApi.middleware,
      identifyApi.middleware,
      onboardingCallCreditsApi.middleware,
      reviewCreditsApi.middleware,
      productHuntCreditsApi.middleware,
      g2ReviewCreditsApi.middleware,
      adminOrganizationsApi.middleware,
      adminCreditUsageApi.middleware,
      adminCardTrialsApi.middleware,
      adminFinanceApi.middleware,
      adminRevenueApi.middleware,
      adminCluChatsApi.middleware,
      cluSharingApi.middleware,
      slackApi.middleware,
      workspaceConnectionsApi.middleware,
      pluginProspectApi.middleware,
      pluginAgentsApi.middleware,
      engageApi.middleware,
      knowledgeApi.middleware,
      cluMemoryApi.middleware,
      homeCardsApi.middleware
    ),
  // RTK Query's results reach React through batched store notifications,
  // queued by default on requestAnimationFrame. A tab that draws no frames (a
  // background tab, an occluded window, a hard load before its first paint)
  // never ran that callback, so the entitlements answer sat in the store
  // while every gate waited on it: agent pages hung on their skeleton until
  // something painted. A short timer keeps the batching without the frame.
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers({ autoBatch: { type: 'timer', timeout: 16 } }),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
